// ═══════════════════════════════════════════════════════════════════════════════
// GIFTING PAGE - Create gift boxes from cart products
// ═══════════════════════════════════════════════════════════════════════════════
//
// CSS-first approach: JS only toggles data-* attributes on elements.
// All visual states (selected, assigned, empty, loading) are handled
// by Tailwind v4 data-[*] variants in the Liquid template.
//
// ═══════════════════════════════════════════════════════════════════════════════

(function () {
  "use strict";

  let selectedBoxType = null;
  let selectedVariants = new Map(); // variantId -> count (supports same product x2)
  let cart = null;
  let eligibleHandles = new Set(); // product handles with "gift-box-options" tag

  const $ = (id) => document.getElementById(id);
  const giftingData = $("gifting-data");
  const translations = $("theme-translations");
  const productSelector = $("product-selector");
  const productSection = $("product-section");
  const giftBoxesList = $("gift-boxes-list");
  const giftBoxesSection = $("gift-boxes-section");
  const createBtn = $("create-gift-box-btn");
  const resetBtn = $("reset-gift-boxes-btn");
  const tmplProductCard = $("tmpl-product-card");
  const tmplGiftBoxItem = $("tmpl-gift-box-item");

  const giftProducts = {
    normal: { variantId: giftingData?.dataset.normalBoxVariantId || null },
    ramadan: { variantId: giftingData?.dataset.ramadanBoxVariantId || null },
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  function t(key) {
    if (!translations) return key;
    const dataKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    return translations.dataset[dataKey] || key;
  }

  const shopCurrency = giftingData?.dataset.shopCurrency || "AED";

  function formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: shopCurrency,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function getAssignedSkuCounts() {
    if (!cart) return new Map();
    const counts = new Map();
    for (const item of cart.items) {
      if (item.properties?._gift_box_type) {
        if (item.properties._for_product_1) {
          const sku = item.properties._for_product_1;
          counts.set(sku, (counts.get(sku) || 0) + 1);
        }
        if (item.properties._for_product_2) {
          const sku = item.properties._for_product_2;
          counts.set(sku, (counts.get(sku) || 0) + 1);
        }
      }
    }
    return counts;
  }

  function getTotalSelected() {
    let total = 0;
    selectedVariants.forEach((count) => (total += count));
    return total;
  }

  function getRegularItems() {
    if (!cart) return [];
    return cart.items.filter(
      (item) =>
        !item.properties ||
        (item.properties._is_gift_option !== "true" &&
          item.properties._is_free_gift !== "true" &&
          !item.properties._gift_box_type),
    );
  }

  function getGiftBoxItems() {
    if (!cart) return [];
    return cart.items.filter((item) => item.properties?._gift_box_type);
  }

  function findCartItemBySku(sku) {
    if (!cart) return null;
    return cart.items.find((item) => item.sku === sku);
  }

  async function fetchEligibleProducts(items) {
    const uniqueHandles = [...new Set(items.map((item) => item.handle).filter(Boolean))];
    eligibleHandles = new Set();

    await Promise.allSettled(
      uniqueHandles.map((handle) =>
        fetch(`/products/${handle}.json`)
          .then((r) => r.json())
          .then((data) => {
            const tags = (data.product?.tags || "")
              .split(",")
              .map((tag) => tag.trim().toLowerCase());
            if (tags.includes("gift-box-options")) {
              eligibleHandles.add(handle);
            }
          }),
      ),
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  function renderProductCards() {
    const items = getRegularItems();
    const assignedCounts = getAssignedSkuCounts();

    // Toggle data-empty on parent — CSS handles show/hide of grid vs empty msg
    if (items.length === 0) {
      productSection.dataset.empty = "";
    } else {
      delete productSection.dataset.empty;
    }

    productSelector.innerHTML = "";

    for (const item of items) {
      const vid = String(item.variant_id);
      const sku = item.sku || "";
      const assignedCount = assignedCounts.get(sku) || 0;
      const availableQty = item.quantity - assignedCount;
      const selectCount = selectedVariants.get(vid) || 0;
      const isFullyAssigned = availableQty <= 0;
      const isEligible = eligibleHandles.has(item.handle);

      const clone = tmplProductCard.content.cloneNode(true);
      const btn = clone.querySelector(".gift-product-card");

      btn.dataset.variantId = vid;
      btn.dataset.availableQty = availableQty;

      // Set state — CSS does the rest via data-[state=*] variants
      if (!isEligible) {
        btn.dataset.state = "ineligible";
        btn.disabled = true;
      } else if (isFullyAssigned) {
        btn.dataset.state = "assigned";
        btn.disabled = true;
      } else if (selectCount > 0) {
        btn.dataset.state = "selected";
      }

      const img = btn.querySelector("img");
      if (item.image) {
        img.src = item.image.replace(/(\.\w+)(\?|$)/, "_300x$1$2");
        img.alt = item.product_title || "";
      } else {
        img.remove();
      }

      btn.querySelector('[data-role="title"]').textContent =
        item.product_title || "";
      const priceEl = btn.querySelector('[data-role="price"]');
      priceEl.dataset.price = item.price;
      priceEl.textContent = formatMoney(item.price);

      const badge = btn.querySelector('[data-role="badge"]');
      if (!isEligible) {
        badge.textContent = t("notEligibleForGiftBox");
        badge.dataset.visible = "";
      } else if (isFullyAssigned) {
        badge.textContent = t("productInBox");
        badge.dataset.visible = "";
      } else if (assignedCount > 0) {
        badge.textContent = `${assignedCount}/${item.quantity} ${t("productInBox").toLowerCase()}`;
        badge.dataset.visible = "";
      }

      // Show selection count badge (x1, x2) when product is selected
      const selectCountEl = btn.querySelector('[data-role="select-count"]');
      if (selectCountEl && selectCount > 0) {
        selectCountEl.textContent = `x${selectCount}`;
        selectCountEl.dataset.visible = "";
      }

      productSelector.appendChild(clone);
    }
  }

  function renderGiftBoxes() {
    const boxes = getGiftBoxItems();

    // Toggle data-empty on parent — CSS handles empty msg + reset button visibility
    if (boxes.length === 0) {
      giftBoxesSection.dataset.empty = "";
    } else {
      delete giftBoxesSection.dataset.empty;
    }

    giftBoxesList.innerHTML = "";

    for (const item of boxes) {
      const clone = tmplGiftBoxItem.content.cloneNode(true);
      const card = clone.querySelector(".gift-box-card");

      const boxType = item.properties._gift_box_type || "normal";
      clone.querySelector('[data-role="type-label"]').textContent =
        boxType === "ramadan" ? t("ramadanGiftBox") : t("normalGiftBox");

      const v1Sku = item.properties._for_product_1;
      const v2Sku = item.properties._for_product_2;
      const p1Name = item.properties._product_1_name || "";
      const p2Name = item.properties._product_2_name || "";
      const p1Item = v1Sku ? findCartItemBySku(v1Sku) : null;
      const p2Item = v2Sku ? findCartItemBySku(v2Sku) : null;

      // Set box type — CSS toggles icon via group-data-[box-type=*]
      card.dataset.boxType = boxType;

      // Set count — CSS handles single vs overlapping layout via group-data-[count=*]
      card.dataset.count = v2Sku && p2Name ? "2" : "1";

      const img1 = clone.querySelector('[data-role="img-1"]');
      if (p1Item?.image) {
        img1.src = p1Item.image.replace(/(\.\w+)(\?|$)/, "_150x$1$2");
        img1.alt = p1Name;
      }

      const img2 = clone.querySelector('[data-role="img-2"]');
      if (p2Item?.image) {
        img2.src = p2Item.image.replace(/(\.\w+)(\?|$)/, "_150x$1$2");
        img2.alt = p2Name;
      }

      clone.querySelector('[data-role="name-1"]').textContent = p1Name;
      clone.querySelector('[data-role="name-2"]').textContent = p2Name;

      clone.querySelector(".remove-gift-box-btn").dataset.lineKey = item.key;

      giftBoxesList.appendChild(clone);
    }
  }

  // ── Event Handlers ───────────────────────────────────────────────────────

  function handleBoxTypeSelect(e) {
    const btn = e.target.closest(".gift-box-type-btn");
    if (!btn) return;

    selectedBoxType = btn.dataset.boxType;

    // Toggle data-selected — CSS handles ring/bg via data-[selected]:
    document.querySelectorAll(".gift-box-type-btn").forEach((b) => {
      if (b === btn) {
        b.dataset.selected = "";
      } else {
        delete b.dataset.selected;
      }
    });
  }

  function handleProductSelect(e) {
    const card = e.target.closest(".gift-product-card");
    if (!card || card.disabled) return;

    const variantId = card.dataset.variantId;
    const availableQty = parseInt(card.dataset.availableQty) || 1;
    const currentCount = selectedVariants.get(variantId) || 0;
    const totalSelected = getTotalSelected();

    if (currentCount > 0) {
      // Already selected — try to increment, otherwise deselect
      const canAddMore = currentCount < availableQty && totalSelected < 2;
      if (canAddMore) {
        selectedVariants.set(variantId, currentCount + 1);
      } else {
        selectedVariants.delete(variantId);
      }
    } else {
      if (totalSelected >= 2) {
        alert(t("max2Products"));
        return;
      }
      selectedVariants.set(variantId, 1);
    }

    renderProductCards();
  }

  async function handleCreateGiftBox() {
    if (!selectedBoxType) return alert(t("selectBoxTypeFirst"));
    if (selectedVariants.size === 0) return alert(t("selectAtLeastOne"));

    const boxData = giftProducts[selectedBoxType];
    if (!boxData?.variantId) {
      return alert(
        "Gift box product not found. Please ensure it exists in Shopify.",
      );
    }

    const selected = [];
    selectedVariants.forEach((count, vid) => {
      const item = getRegularItems().find((i) => String(i.variant_id) === vid);
      if (item) {
        for (let c = 0; c < count; c++) selected.push(item);
      }
    });

    const v1 = selected[0];
    const v2 = selected[1] || null;

    // Set loading — CSS hides label, shows loading text, dims button
    createBtn.dataset.loading = "";
    createBtn.disabled = true;

    console.log({ v1, v2 });

    try {
      await addToCart(boxData.variantId, 1, {
        _gift_box_type: selectedBoxType,
        _for_product_1: v1.sku || "",
        _for_product_2: v2 ? v2.sku || "" : "",
        _product_1_name: v1.product_title || "",
        _product_2_name: v2 ? v2.product_title || "" : "",
      });

      // Reset selections
      selectedBoxType = null;
      selectedVariants.clear();
      document.querySelectorAll(".gift-box-type-btn").forEach((b) => {
        delete b.dataset.selected;
      });

      await refreshPage();
    } catch (err) {
      console.error("Failed to create gift box:", err);
    } finally {
      delete createBtn.dataset.loading;
      createBtn.disabled = false;
    }
  }

  async function handleRemoveGiftBox(e) {
    const btn = e.target.closest(".remove-gift-box-btn");
    if (!btn) return;

    btn.disabled = true;
    btn.textContent = "...";

    try {
      await apiLayer.cartUpdate({ [btn.dataset.lineKey]: 0 });
      cartState.invalidate();
      await refreshPage();
      await uiLayer.refreshSections(["header"]);
    } catch (err) {
      console.error("Failed to remove gift box:", err);
      alert(err.message || "Failed to remove gift box");
    }
  }

  async function handleResetGiftBoxes() {
    const boxes = getGiftBoxItems();
    if (boxes.length === 0) return;
    if (!confirm(t("resetAllConfirm"))) return;

    resetBtn.disabled = true;
    resetBtn.textContent = "...";

    try {
      const updates = {};
      boxes.forEach((item) => (updates[item.key] = 0));

      await apiLayer.cartUpdate(updates);
      cartState.invalidate();
      await refreshPage();
      await uiLayer.refreshSections(["header"]);
    } catch (err) {
      console.error("Failed to reset gift boxes:", err);
      alert(err.message || "Failed to reset gift boxes");
    } finally {
      resetBtn.disabled = false;
      resetBtn.textContent = t("resetAll");
    }
  }

  // ── Refresh & Init ───────────────────────────────────────────────────────

  async function refreshPage() {
    try {
      cart = await (await fetch("/cart.js")).json();
      await fetchEligibleProducts(getRegularItems());
      renderProductCards();
      renderGiftBoxes();
    } catch (err) {
      console.error("Failed to refresh gifting page:", err);
    }
  }

  async function init() {
    try {
      cart = await (await fetch("/cart.js")).json();
    } catch (err) {
      console.error("Failed to fetch cart:", err);
      return;
    }

    await fetchEligibleProducts(getRegularItems());
    renderProductCards();
    renderGiftBoxes();

    $("box-type-selector")?.addEventListener("click", handleBoxTypeSelect);
    productSelector?.addEventListener("click", handleProductSelect);
    createBtn?.addEventListener("click", handleCreateGiftBox);
    giftBoxesList?.addEventListener("click", handleRemoveGiftBox);
    resetBtn?.addEventListener("click", handleResetGiftBoxes);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
